import { useId, type ReactNode, type CSSProperties, type ChangeEvent } from 'react';
import * as RadixSwitch from '@radix-ui/react-switch';
import clsx from 'clsx';
import { switchRoot, switchThumb, switchWrapper, switchLabel, switchHelperText } from './switch.css';

export type SwitchSize = 'small' | 'medium';
export type SwitchShape = 'pill' | 'square';

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** MUI-compat: wraps onCheckedChange */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  size?: SwitchSize;
  /** Track/thumb corner shape. Default 'pill'; 'square' for squared corners. */
  shape?: SwitchShape;
  label?: ReactNode;
  /** Label position relative to switch */
  labelPlacement?: 'end' | 'start';
  helperText?: string;
  name?: string;
  style?: CSSProperties;
  className?: string;
  'data-testid'?: string;
  /** Accessible name when there is no visible `label` (e.g. a header provides it). */
  'aria-label'?: string;
}

export function Switch({
  checked,
  defaultChecked,
  onCheckedChange,
  onChange,
  disabled = false,
  size = 'medium',
  shape = 'pill',
  label,
  labelPlacement = 'end',
  helperText,
  name,
  style,
  className,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: SwitchProps) {
  const handleCheckedChange = (val: boolean) => {
    onCheckedChange?.(val);
    if (onChange) {
      const syntheticEvent = { target: { checked: val } } as ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  // F9: programmatically associate the label span with the switch button.
  // Wrapping a button in <label> only works for click; screen readers
  // reading the switch directly need aria-labelledby.
  const labelId = useId();

  const switchEl = (
    <RadixSwitch.Root
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={handleCheckedChange}
      disabled={disabled}
      name={name}
      data-testid={testId}
      className={switchRoot({ size, shape })}
      aria-labelledby={label ? labelId : undefined}
      aria-label={!label ? ariaLabel : undefined}
    >
      <RadixSwitch.Thumb className={switchThumb({ size, shape })} />
    </RadixSwitch.Root>
  );

  if (!label && !helperText) {
    return (
      <div style={style} className={className}>
        {switchEl}
      </div>
    );
  }

  // F9 (secondary): previously `disabled && 'disabled'` emitted a literal
  // "disabled" className. Use a `data-disabled` attribute instead — that's
  // observable for styling (`label[data-disabled] { ... }`) without leaking
  // a fake CSS-module class string into the DOM.
  return (
    <div style={style} className={className}>
      <label
        className={clsx(switchWrapper)}
        data-disabled={disabled ? 'true' : undefined}
      >
        {labelPlacement === 'start' && <span id={labelId} className={switchLabel}>{label}</span>}
        {switchEl}
        {labelPlacement === 'end' && label && <span id={labelId} className={switchLabel}>{label}</span>}
      </label>
      {helperText && <span className={switchHelperText}>{helperText}</span>}
    </div>
  );
}
